/**
 * Sentry init for `@shopista/web` (T12.0.22).
 *
 * Gated on the build-time `VITE_SENTRY_DSN` env var. Vite bakes env vars
 * into the bundle at build time; runtime env doesn't flow. When the DSN is
 * unset (local dev) this module is a no-op — no `Sentry.init` call, no
 * console noise, no SDK side effects beyond loading.
 *
 * Import as the FIRST statement of `main.tsx` so module-load errors in the
 * rest of the entry are captured.
 *
 * Companion: backend ships errors to the `shopista-backend-core` project via
 * the existing `SENTRY_DSN` env var (see `backend/src/index.ts`). This file
 * ships frontend errors to `shopista-front` on the same Sentry org. See
 * [health-and-logs.md](../../../docs/for-developers/health-and-logs.md).
 */

import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';

const DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;

if (DSN) {
  Sentry.init({
    dsn: DSN,
    // BUILD CHANNEL (`dev` / `production` / `local`), NOT Vite's `import.meta.env.MODE`.
    // MODE is `production` for ANY optimized `vite build` — including the dev-target
    // image (which runs `vite build`), so MODE would tag dev errors as `production`
    // and pollute the production error stream. `__APP_BUILD__.channel` is the
    // build-arg-baked channel the version display already reads (see appVersion.ts /
    // vite.config.ts); its local fallback is `local`, mirrored here when unset.
    environment: __APP_BUILD__?.channel || 'local',
    // Match the backend's 10% sample rate so the two halves of a user
    // session correlate proportionally. Defaults are otherwise fine —
    // advanced features (session replay, breadcrumbs tuning) deferred per
    // T12.0.22 § Out of Scope.
    tracesSampleRate: 0.1,
  });

  // T50 (9b) — tag every Sentry event with the SAME platform value PostHog's
  // super-property carries: `android` / `ios` / `web` from
  // `Capacitor.getPlatform()` (NOT the boolean isNativePlatform()). The two
  // native platforms stay distinguishable, and an error is filterable by the
  // same `platform` dimension analytics uses. `getPlatform()` always returns
  // one of the three strings.
  Sentry.setTag('platform', Capacitor.getPlatform());
}

/**
 * Re-export of `@sentry/react` so consumers (e.g. `sseSubscriber`) capture
 * exceptions through a single shared SDK instance. Calls are safe no-ops
 * when `Sentry.init` did not run (the SDK defaults to a NoopClient until
 * `init` configures it).
 */
export { Sentry };
